/**
 * URL query-parameter feature flags for runtime toggles.
 *
 * Usage: append `?amenityBooking=true` to any page URL to enable
 * the Amenity Booking feature. The flag is persisted in sessionStorage
 * so it survives navigation within the same browser tab.
 *
 * SECURITY: these flags are intentionally advisory — any same-origin
 * script (stored XSS, browser extension, devtools paste) can write
 * `sessionStorage` directly to flip them. Use them only for dev-time
 * opt-in to pre-GA features. Promotion to GA requires a server-side gate
 * (community-id allowlist, role gate, or a feature-flag service); never
 * rely on these flags as a security boundary.
 *
 * Cleared on logout via `AuthService.clearAuthLocalStorageData()`, which
 * is also invoked on the 401-response path through `ServerErrorService`,
 * so flags do not leak across user sessions on the same tab.
 *
 * Remove this file once the Amenity Booking feature is GA.
 */

export const STORAGE_PREFIX = 'ff_';

function isFeatureEnabled(param: string): boolean {
  // 1. Check current URL — if present, persist and return
  const params = new URLSearchParams(window.location.search);
  const urlValue = params.get(param);
  if (urlValue !== null) {
    sessionStorage.setItem(STORAGE_PREFIX + param, urlValue);
    return urlValue === 'true';
  }

  // 2. Fall back to sessionStorage (survives in-app navigation)
  return sessionStorage.getItem(STORAGE_PREFIX + param) === 'true';
}

// Returns the explicit boolean if the user set the flag via URL param or has
// a value stored in sessionStorage, or `null` when no opt-in has been recorded
// for the current tab. Callers that want a dev-default short-circuit must
// consult this first so an explicit `?<param>=false` can pin the value off.
function readExplicitFeatureFlag(param: string): boolean | null {
  const params = new URLSearchParams(window.location.search);
  const urlValue = params.get(param);
  if (urlValue !== null) {
    sessionStorage.setItem(STORAGE_PREFIX + param, urlValue);
    return urlValue === 'true';
  }
  const stored = sessionStorage.getItem(STORAGE_PREFIX + param);
  return stored !== null ? stored === 'true' : null;
}

export function isDevOrLocal(): boolean {
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === 'resident.dev.ciranet.com' ||
    h === 'resident.local.ciranet.com'
  );
}

export function isAmenityBookingEnabled(): boolean {
  // Always on in dev/local, URL param required elsewhere
  if (isDevOrLocal()) return true;
  return isFeatureEnabled('amenityBooking');
}

export function isAmenityBookingMfeEnabled(): boolean {
  // An explicit URL/sessionStorage value always wins, so a dev can pin
  // `?amenityBookingMfe=false` to exercise the native amenity-booking path
  // for a session. With no explicit value, dev/local auto-enable the MFE so
  // the menu link routes straight to the deployed bundle. QA/prod fall back
  // to `false` unless the URL flag is explicitly set — the URL-flag opt-in
  // remains the only production-side gate until a server-side gate ships.
  const explicit = readExplicitFeatureFlag('amenityBookingMfe');
  if (explicit !== null) return explicit;
  return isDevOrLocal();
}

export function isCalendarMfeEnabled(): boolean {
  // Explicit opt-in only — requires `?calendarMfe=true` in the URL.
  // When enabled, the community calendar route loads the MFE version
  // instead of the native CalendarComponent.
  return isFeatureEnabled('calendarMfe');
}
